import { Button, DropdownMenu, Input, Prompt, Tabs, toast } from "@medusajs/ui"
import { EllipsisHorizontal } from "@medusajs/icons"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useMemo, useState } from "react"
import { apiFetch, errorMessage } from "@/lib/sdk"

/**
 * Shopify's list tabs + saved views: fixed tabs (All, Active, …) followed by
 * views staff saved from the current filters. A view is just the list's URL
 * filters, shared by every staff member (stored on the store).
 */
export type FixedTab = { value: string; label: React.ReactNode; params: Record<string, string> }
export type SavedView = { id: string; name: string; params: Record<string, string> }
type Resource = "products" | "orders" | "customers"

const IGNORED = new Set(["page", "view"])
const filtersOf = (p: URLSearchParams) => Object.fromEntries([...p.entries()].filter(([k, v]) => !IGNORED.has(k) && v !== ""))
const same = (a: Record<string, string>, b: Record<string, string>) => {
  const ka = Object.keys(a).sort()
  const kb = Object.keys(b).sort()
  return ka.length === kb.length && ka.every((k, i) => k === kb[i] && a[k] === b[k])
}

export function useSavedViews(resource: Resource) {
  return useQuery({ queryKey: ["list-views", resource], queryFn: () => apiFetch<{ views: SavedView[] }>(`/admin/terify/${resource}/views`), staleTime: 60_000 })
}

export function ViewTabs({
  resource,
  tabs,
  params,
  setParams,
  canSave,
}: {
  resource: Resource
  tabs: FixedTab[]
  params: URLSearchParams
  setParams: (p: URLSearchParams) => void
  canSave: boolean
}) {
  const qc = useQueryClient()
  const views = useSavedViews(resource).data?.views ?? []
  const current = filtersOf(params)
  const viewId = params.get("view") ?? ""
  const activeView = views.find((v) => v.id === viewId)
  const fixed = tabs.find((t) => same(t.params, current))
  const active = activeView ? `view:${activeView.id}` : fixed ? `tab:${fixed.value}` : ""
  const changed = activeView ? !same(activeView.params, current) : !fixed
  const [naming, setNaming] = useState<null | "new" | "rename">(null)
  const [deleting, setDeleting] = useState(false)

  const save = useMutation({
    mutationFn: (v: { name: string; params: Record<string, string>; id?: string }) =>
      apiFetch<{ views: SavedView[] }>(`/admin/terify/${resource}/views${v.id ? `?id=${encodeURIComponent(v.id)}` : ""}`, { method: "POST", body: JSON.stringify({ name: v.name, params: v.params }) }),
    onSuccess: (r) => qc.setQueryData(["list-views", resource], r),
  })
  const del = useMutation({
    mutationFn: (id: string) => apiFetch<{ views: SavedView[] }>(`/admin/terify/${resource}/views?id=${encodeURIComponent(id)}`, { method: "DELETE" }),
    onSuccess: (r) => qc.setQueryData(["list-views", resource], r),
  })

  const go = (value: string) => {
    if (value.startsWith("view:")) {
      const v = views.find((x) => `view:${x.id}` === value)
      if (v) setParams(new URLSearchParams({ ...v.params, view: v.id }))
    } else {
      const t = tabs.find((x) => `tab:${x.value}` === value)
      if (t) setParams(new URLSearchParams(t.params))
    }
  }

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1">
      <Tabs value={active} onValueChange={go}>
        <Tabs.List className="flex-wrap">
          {tabs.map((t) => (
            <Tabs.Trigger key={t.value} value={`tab:${t.value}`}>
              {t.label}
            </Tabs.Trigger>
          ))}
          {views.map((v) => (
            <Tabs.Trigger key={v.id} value={`view:${v.id}`}>
              {v.name}
            </Tabs.Trigger>
          ))}
        </Tabs.List>
      </Tabs>
      {canSave && (
        <>
          {changed && Object.keys(current).length > 0 && (
            <Button
              size="small"
              variant="transparent"
              isLoading={save.isPending}
              onClick={() =>
                activeView
                  ? save.mutate({ id: activeView.id, name: activeView.name, params: current }, { onSuccess: () => toast.success("View saved"), onError: (e) => toast.error(errorMessage(e)) })
                  : setNaming("new")
              }
            >
              {activeView ? "Save view" : "Save as view"}
            </Button>
          )}
          {activeView && (
            <DropdownMenu>
              <DropdownMenu.Trigger asChild>
                <button type="button" aria-label={`Options for view ${activeView.name}`} className="flex h-7 w-7 items-center justify-center rounded-[8px] text-[var(--tb-ink-3,#616161)] hover:bg-black/5">
                  <EllipsisHorizontal />
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Content align="start">
                <DropdownMenu.Item onClick={() => setNaming("rename")}>Rename view</DropdownMenu.Item>
                <DropdownMenu.Item onClick={() => setNaming("new")}>Duplicate view</DropdownMenu.Item>
                <DropdownMenu.Separator />
                <DropdownMenu.Item className="text-ui-fg-error" onClick={() => setDeleting(true)}>Delete view</DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu>
          )}
        </>
      )}
      {naming && (
        <NameDialog
          title={naming === "rename" ? "Rename view" : "Save as new view"}
          initial={naming === "rename" ? activeView?.name ?? "" : ""}
          busy={save.isPending}
          onClose={() => setNaming(null)}
          onSave={(name) =>
            save.mutate(naming === "rename" && activeView ? { id: activeView.id, name, params: activeView.params } : { name, params: current }, {
              onSuccess: (r) => {
                toast.success(naming === "rename" ? "View renamed" : "View saved")
                setNaming(null)
                const made = naming === "new" ? r.views[r.views.length - 1] : null
                if (made) setParams(new URLSearchParams({ ...made.params, view: made.id }))
              },
              onError: (e) => toast.error(errorMessage(e)),
            })
          }
        />
      )}
      <Prompt open={deleting} onOpenChange={setDeleting}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>Delete view "{activeView?.name}"?</Prompt.Title>
            <Prompt.Description>The view is removed for all staff. The {resource} themselves are not changed.</Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel>Cancel</Prompt.Cancel>
            <Prompt.Action
              onClick={() =>
                activeView &&
                del.mutate(activeView.id, {
                  onSuccess: () => {
                    toast.success("View deleted")
                    setParams(new URLSearchParams(tabs[0]?.params ?? {}))
                  },
                  onError: (e) => toast.error(errorMessage(e)),
                })
              }
            >
              Delete
            </Prompt.Action>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    </div>
  )
}

function NameDialog({ title, initial, busy, onClose, onSave }: { title: string; initial: string; busy: boolean; onClose: () => void; onSave: (name: string) => void }) {
  const [name, setName] = useState(initial)
  return (
    <Prompt open onOpenChange={(o) => !o && onClose()}>
      <Prompt.Content>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (name.trim()) onSave(name.trim())
          }}
        >
          <Prompt.Header>
            <Prompt.Title>{title}</Prompt.Title>
            <Prompt.Description>Saved views appear as tabs for every staff member.</Prompt.Description>
          </Prompt.Header>
          <div className="px-6 pb-4">
            <Input autoFocus aria-label="View name" placeholder="e.g. Low stock vials" maxLength={40} value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <Prompt.Footer>
            <Prompt.Cancel type="button">Cancel</Prompt.Cancel>
            <Button type="submit" size="small" isLoading={busy} disabled={!name.trim()}>
              Save
            </Button>
          </Prompt.Footer>
        </form>
      </Prompt.Content>
    </Prompt>
  )
}

/** Column picker: which optional columns this browser shows (remembered per list). */
export function useColumns(key: string, all: { key: string; label: string; default?: boolean }[]) {
  const storageKey = `terify:cols:${key}`
  const defaults = useMemo(() => all.filter((c) => c.default !== false).map((c) => c.key), [all])
  // Stored as { v: visible keys, k: every column key known then }, so a column added later that is
  // on by default shows up for people who already customised their columns (it isn't in `k` yet).
  const [visible, setVisible] = useState<string[]>(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(storageKey) ?? "null")
      if (Array.isArray(raw)) return [...new Set([...raw, ...defaults])] // older format: show new defaults once
      if (raw && Array.isArray(raw.v)) {
        const known = new Set<string>(Array.isArray(raw.k) ? raw.k : [])
        return [...raw.v, ...defaults.filter((k) => !known.has(k) && !raw.v.includes(k))]
      }
      return defaults
    } catch {
      return defaults
    }
  })
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify({ v: visible, k: all.map((c) => c.key) }))
    } catch {
      // private mode: keep in memory only
    }
  }, [storageKey, visible, all])
  const toggle = (k: string) => setVisible((v) => (v.includes(k) ? v.filter((x) => x !== k) : [...v, k]))
  const menu = (
    <DropdownMenu>
      <DropdownMenu.Trigger asChild>
        <button type="button" aria-label="Choose columns" title="Columns" className="flex h-8 w-8 items-center justify-center rounded-[8px] text-[var(--tb-ink-3,#616161)] shadow-[0_0_0_1px_var(--tb-line-strong,#d4d4d4)] hover:bg-black/5">
          <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><rect x="2.75" y="3.75" width="14.5" height="12.5" rx="2" /><path d="M7.75 3.75v12.5M12.25 3.75v12.5" /></svg>
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="end">
        <DropdownMenu.Label>Columns</DropdownMenu.Label>
        {all.map((c) => (
          <DropdownMenu.CheckboxItem key={c.key} checked={visible.includes(c.key)} onCheckedChange={() => toggle(c.key)} onSelect={(e) => e.preventDefault()}>
            {c.label}
          </DropdownMenu.CheckboxItem>
        ))}
        <DropdownMenu.Separator />
        <DropdownMenu.Item onClick={() => setVisible(defaults)}>Reset to default</DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu>
  )
  return { visible, menu, isOn: (k: string) => visible.includes(k) }
}

/**
 * "Sort by" menu next to the column picker (Shopify's ⇅ button): one choice from `options`.
 * The first option is the default; the button is highlighted while another one is active.
 */
export function SortMenu({ value, options, onChange }: { value: string; options: [string, string][]; onChange: (v: string) => void }) {
  const active = !!value && value !== options[0]?.[0]
  const current = options.find(([v]) => v === value)?.[1] ?? options[0]?.[1]
  return (
    <DropdownMenu>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={`Sort: ${current}`}
          title={`Sort: ${current}`}
          className={`flex h-8 w-8 items-center justify-center rounded-[8px] text-[var(--tb-ink-3,#616161)] hover:bg-black/5 ${active ? "bg-[var(--tb-info-bg,#eaf4ff)] shadow-[0_0_0_1px_#8ab9ee]" : "shadow-[0_0_0_1px_var(--tb-line-strong,#d4d4d4)]"}`}
        >
          <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6.5 3.5v13M3.5 6.5l3-3 3 3M13.5 16.5v-13M10.5 13.5l3 3 3-3" /></svg>
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="end" className="max-h-[70vh] overflow-y-auto">
        <DropdownMenu.Label>Sort by</DropdownMenu.Label>
        <DropdownMenu.RadioGroup value={value || options[0]?.[0]} onValueChange={onChange}>
          {options.map(([v, l]) => (
            <DropdownMenu.RadioItem key={v} value={v}>{l}</DropdownMenu.RadioItem>
          ))}
        </DropdownMenu.RadioGroup>
      </DropdownMenu.Content>
    </DropdownMenu>
  )
}
